import React from "react";
import { Link } from "react-router-dom";

import heroWebp from "../../images/examples/salon-example.webp";
import heroJpg from "../../images/examples/salon-example.jpg";

// The right-hand visual is a photograph of a display on the wall of a salon
// waiting area, above two armchairs.
// Not a product screenshot: the thing that makes WallBurst unlike every other
// advertising platform is that the surface is physical, and a rectangle of
// dashboard UI says the opposite.
//
// --- why this photograph, and not the one that was here -------------------
//
// It was a screen in a café window at dusk with someone walking past on the
// pavement. A good photograph of the OLD model: a screen facing the street,
// seen for as long as it takes to walk past it. WallBurst is now a network of
// screens INSIDE businesses, and the hero image is the loudest claim on the
// site -- louder than any sentence next to it. A visitor who reads "inside
// real businesses" over a picture of a shop window taken from the sidewalk
// believes the picture.
//
// So the hero is the salon instead: indoor, on a wall, with seating in frame.
// The armchairs are the argument. They say somebody is sitting here for half
// an hour, which is the whole reason an indoor screen is worth buying and the
// one thing a storefront window cannot claim.
//
// KNOWN COMPROMISE: this source is 546px wide against a 560px slot, so it is
// sharp at 1x and soft on a 2x display -- the photograph it replaced was
// 1448px. Being on-message at 1x beats being off-message at 2x, but the real
// fix is a commissioned indoor hero shot at ~1400px, and that is now the only
// outstanding item on the photography list: the rail below has its own four
// pictures, so nothing is shared between the two sections any more.
//
// The advertisement inside the photograph is invented ("Look Good Feel
// Amazing"). A fake logo on a demo screen is a claim about a customer, which
// is the same rule the RealWorld rail below follows.
//
// It is NOT eagerly hinted or preloaded despite being above the fold: it is
// one small request against a headline that is already legible without it.

export default function Hero() {
    return (
        <section className="hero">
            <div className="hero__grid">
                <div className="hero__copy">
                    <h1 className="hero__title">
                        Put yourself<br />
                        out there.
                    </h1>
                    <p className="hero__lede">
                        Advertise on screens inside real businesses and local
                        places — right where people spend their time.
                    </p>
                    <div className="hero__actions">
                        <Link to="/devices" className="wb-btn wb-btn--primary wb-btn--lg">
                            Explore locations
                        </Link>
                        <Link to="/waitlist" className="wb-btn wb-btn--secondary wb-btn--lg">
                            Become a host
                        </Link>
                    </div>
                </div>

                <div className="hero__stage">
                    <figure className="hero__shot">
                        <picture>
                            <source srcSet={heroWebp} type="image/webp" />
                            <img
                                className="hero__photo"
                                src={heroJpg}
                                alt="A large display on the wall of a salon waiting area,
                                     above two armchairs and a side table."
                                width="546"
                                height="452"
                                decoding="async"
                            />
                        </picture>
                    </figure>

                    {/* One piece of product UI, overhanging the photograph so the
                        physical and the software read as one thing.

                        There were two. "Playing now", with a pulsing green dot,
                        implied live data that the page does not have -- it was
                        marked illustrative for screen readers and to nobody
                        else. A visitor who works out that a liveness indicator
                        is decorative has been given a reason to discount the
                        number next to it, and the rate is the number that
                        matters here.

                        It hangs off the left edge rather than sitting inside
                        the frame: that is the side the headline is on, so it
                        bridges the two columns instead of decorating one. */}
                    <div className="hero__chip hero__chip--book">
                        <span className="hero__chip-title">Available</span>
                        <span className="hero__chip-rate">from $25 / day</span>
                    </div>
                </div>
            </div>
        </section>
    );
}
